/* eslint-disable @typescript-eslint/no-explicit-any */
import Highcharts from "highcharts";

export default function betterAxisTick(H: typeof Highcharts) {
  H.wrap(H.Tick.prototype, "addLabel", function (this: Highcharts.Tick) {
    const tick = this as any;
    const axis = tick.axis,
      options = axis.options,
      chart = axis.chart,
      categories = axis.categories,
      log = axis.logarithmic,
      names = axis.names,
      pos = tick.pos,
      labelOptions = Highcharts.pick(tick.options?.labels, options.labels),
      tickPositions = axis.tickPositions,
      isFirst = pos === tickPositions[0],
      isLast = pos === tickPositions[tickPositions.length - 1],
      animateLabels =
        !options.disableAnimation && // HERE is the METROSCOPE edit <<<<<<<<<<=====================
        (!labelOptions.step || labelOptions.step === 1) &&
        axis.tickInterval === 1,
      tickPositionInfo = tickPositions.info;

    let label = tick.label,
      dateTimeLabelFormat,
      dateTimeLabelFormats,
      i: number;

    // The context value
    let value =
      tick.parameters.category ||
      (categories ? Highcharts.pick(categories[pos], names[pos], pos) : pos);
    if (log && Highcharts.isNumber(value)) {
      value = Highcharts.correctFloat(log.lin2log(value));
    }

    // Set the datetime label format. If a higher rank is set for this
    // position, use that. If not, use the general format.
    if (axis.dateTime && tickPositionInfo) {
      dateTimeLabelFormats = chart.time.resolveDTLFormat(
        options.dateTimeLabelFormats[
          (!options.grid && tickPositionInfo.higherRanks[pos]) || tickPositionInfo.unitName
        ],
      );
      dateTimeLabelFormat = dateTimeLabelFormats.main;
    }

    // set properties for access in render method
    /**
     * True if the tick is the first one on the axis.
     * @name Highcharts.Tick#isFirst
     * @readonly
     * @type {boolean|undefined}
     */
    tick.isFirst = isFirst;
    /**
     * True if the tick is the last one on the axis.
     * @name Highcharts.Tick#isLast
     * @readonly
     * @type {boolean|undefined}
     */
    tick.isLast = isLast;

    // Get the string
    const ctx = {
      axis,
      chart,
      dateTimeLabelFormat,
      isFirst,
      isLast,
      pos,
      tick,
      tickPositionInfo,
      value,
    };

    // Fire an event that allows modifying the context for use in
    // `labels.format` and `labels.formatter`.
    Highcharts.fireEvent(this, "labelFormat", ctx);

    // Label formatting. When `labels.format` is given, we first run the
    // defaultFormatter and append the result to the context as `text`.
    // Handy for adding prefix or suffix while keeping default number
    // formatting.
    const labelFormatter = (ctx: Highcharts.AxisLabelsFormatterContextObject): string => {
      if (labelOptions.formatter) {
        return labelOptions.formatter.call(ctx, ctx);
      }
      if (labelOptions.format) {
        ctx.text = axis.defaultLabelFormatter.call(ctx);
        return Highcharts.format(labelOptions.format, ctx, chart);
      }
      return axis.defaultLabelFormatter.call(ctx, ctx);
    };
    const str = labelFormatter.call(ctx, ctx as any);

    // Set up conditional formatting based on the format list if existing.
    const list = dateTimeLabelFormats?.list;
    if (list) {
      tick.shortenLabel = function (): void {
        for (i = 0; i < list.length; i++) {
          Highcharts.extend(ctx, { dateTimeLabelFormat: list[i] });
          label.attr({
            text: labelFormatter.call(ctx, ctx as any),
          });
          if (label.getBBox().width < axis.getSlotWidth(tick) - 2 * labelOptions.padding) {
            return;
          }
        }
        label.attr({
          text: "",
        });
      };
    } else {
      // #15692
      tick.shortenLabel = void 0;
    }

    // Call only after first render
    if (animateLabels && axis._addedPlotLB) {
      tick.moveLabel(str, labelOptions);
    }
    // First call
    if (!Highcharts.defined(label) && !tick.movedLabel) {
      /**
       * The rendered text label of the tick.
       * @name Highcharts.Tick#label
       * @type {Highcharts.SVGElement|undefined}
       */
      tick.label = label = tick.createLabel({ x: 0, y: 0 }, str, labelOptions);

      // Base value to detect change for new calls to getBBox
      tick.rotation = 0;

      // New feature from Metroscrope to handle click on labels <<<<<<<<<<==========================
      if (axis.userOptions.labels?.onClick) {
        tick.label.element.addEventListener("click", (event: MouseEvent) => {
          event.preventDefault();
          axis.userOptions.labels.onClick(event, { value });
        });
      }
      // end of Metroscope change ==================================================================

      // update
    } else if (label && label.textStr !== str && !animateLabels) {
      // When resetting text, also reset the width if dynamically set
      // (#8809)
      if (label.textWidth && !labelOptions.style.width && !label.styles.width) {
        label.css({ width: null as any });
      }

      label.attr({ text: str });

      label.textPxLength = label.getBBox().width;
    }
  } as Highcharts.WrapProceedFunction);
}
